import { useContext, useMemo } from 'react';
import {
  PAGE_CATALOG,
  pagesOpenedBy,
  tabsOpenedBy,
  type PageDefinition,
  type TabDefinition,
} from '@obraplan/core/domain/access/pages';
import type { PermissionKey } from '@obraplan/core/domain/access/permissions';
import type { ProjectId } from '@obraplan/core/domain/shared/ids';
import type { Session } from '@obraplan/core/application/session';
import type { Container } from '@/app/container';
import { ContainerContext, SessionContext, type SessionState } from './session-context';

export function useContainer(): Container {
  const container = useContext(ContainerContext);
  if (container === undefined) {
    throw new Error('useContainer precisa estar dentro de <AppProviders>.');
  }
  return container;
}

export function useSessionState(): SessionState {
  const state = useContext(SessionContext);
  if (state === undefined) {
    throw new Error('useSessionState precisa estar dentro de <AppProviders>.');
  }
  return state;
}

/** Sessão garantida. Só use dentro das rotas protegidas por `RequireSession`. */
export function useSession(): Session {
  const { session } = useSessionState();
  if (session === undefined) {
    throw new Error('Nenhum perfil ativo. Esta tela exige <RequireSession>.');
  }
  return session;
}

/**
 * Permissão do perfil ativo — usada **apenas para esconder ações** na interface.
 *
 * Nenhuma regra depende disto: o caso de uso verifica sempre, ainda que a ação tenha
 * sido disparada por um caminho que a interface não previa (RN-134).
 */
export function usePermission(permission: PermissionKey, projectId?: ProjectId): boolean {
  const container = useContainer();
  const { session } = useSessionState();
  if (session === undefined) return false;
  return container.permissions.can(session, permission, projectId);
}

/**
 * A permissão existe em **alguma** obra do perfil.
 *
 * É o que a navegação pergunta: o item de menu some para quem nunca poderá abri-lo, e
 * permanece para quem o alcança em pelo menos uma obra — sem piscar a cada troca de obra
 * no seletor. Continua servindo só para esconder (RN-134).
 */
export function usePermissionsAnywhere(): ReadonlySet<PermissionKey> {
  const container = useContainer();
  const { session } = useSessionState();
  if (session === undefined) return EMPTY_PERMISSIONS;
  return container.permissions.allAnywhere(session);
}

/**
 * A permissão vem de papel **global** — o que autoriza operação de sistema.
 *
 * Usada para oferecer, ou não, a opção "todas as obras" onde ela existe. Continua
 * servindo só para esconder: quem recusa é o caso de uso (RN-134).
 */
export function usePermissionGlobally(permission: PermissionKey): boolean {
  const container = useContainer();
  const { session } = useSessionState();
  if (session === undefined) return false;
  return container.permissions.canGlobally(session, permission);
}

/**
 * As telas que o perfil abre, em alguma obra — na ordem do menu.
 *
 * É o que o menu lista e o que a rota confere. Como `usePermissionsAnywhere`, serve para
 * navegar e não autoriza nada: a tela aberta ainda depende das leituras que ela faz, e
 * quem recusa é o caso de uso (RN-134).
 */
export function useVisiblePages(): readonly PageDefinition[] {
  const container = useContainer();
  const { session } = useSessionState();
  return useMemo(
    () => (session === undefined ? [] : pagesOpenedBy(container.permissions.allAnywhere(session))),
    [container, session],
  );
}

/**
 * As abas da tela que o perfil vê, em alguma obra — na ordem da tela (RN-626).
 *
 * O mesmo recorte do menu, um nível abaixo: a aba desmarcada no papel some da faixa. Serve
 * para mostrar e não autoriza nada (RN-134).
 */
export function useVisibleTabs(pagePath: string): readonly TabDefinition[] {
  const container = useContainer();
  const { session } = useSessionState();
  return useMemo(() => {
    const page = PAGE_CATALOG.find((candidate) => candidate.path === pagePath);
    if (session === undefined || page === undefined) return [];
    return tabsOpenedBy(page, container.permissions.allAnywhere(session));
  }, [container, session, pagePath]);
}

const EMPTY_PERMISSIONS: ReadonlySet<PermissionKey> = new Set();
